import { t } from '@lingui/macro';
import { FormationCreatedFrom } from '@prismo-io/schemas';
import { z } from 'zod';

export const LevelSchema = z.object(
  {
    id: z.string().uuid(),
    name: z.string(),
  },
  {
    required_error: t`Le niveau est obligatoire.`,
    invalid_type_error: t`Le niveau n'est pas valide.`,
  }
);

export const AppellationSchema = z.object({
  /* id: z.string().uuid(),
  label: z.string(), */
  code: z.string().trim().min(1),
  referential_id: z.string().trim().uuid(),
  name: z.string().trim().min(1),
});

export const FormationBaseSchema = () =>
  z.object({
    id: z.string().uuid().nullish(),
    name: z
      .string({
        required_error: t`L'intitulé est obligatoire.`,
        invalid_type_error: t`L'intitulé n'est pas valide.`,
      })
      .trim()
      .min(1, t`L'intitulé est obligatoire.`)
      .max(250, t`L'intitulé ne peut pas faire plus de 250 caractères.`),
    level: LevelSchema.nullable(), // z.string({ required_error: 'Le niveau est obligatoire.', invalid_type_error: 'Le niveau n\'est pas valide.' }).trim().min(1, `Le niveau est obligatoire.`),
    favorite: z.string().nullish(),
    yearStart: z
      .date({
        coerce: true,
        invalid_type_error: t`La date n'est pas valide.`,
        required_error: t`La date n'est pas valide.`,
      })
      .nullish(),
    yearEnd: z
      .date({
        coerce: true,
        invalid_type_error: t`La date n'est pas valide.`,
        required_error: t`La date n'est pas valide.`,
      })
      .nullish(),
    establishment: z
      .string({
        invalid_type_error: t`Le nom de l'établissement n'est pas valide.`,
      })
      .trim()
      .nullish(),
    appellations: z.array(AppellationSchema, {
      required_error: t`Vous devez sélectionner au moins 1 métier.`,
    }),
    createdFrom: FormationCreatedFrom.optional(),
  });

export const FormationSchema = () =>
  FormationBaseSchema()
    .refine(
      ({ yearStart: startAt, yearEnd: endAt }) => {
        if (startAt && endAt) {
          const yearStart = startAt.getFullYear();
          const monthStart = startAt.getMonth();
          const yearEnd = endAt.getFullYear();
          const monthEnd = endAt.getMonth();

          return (
            yearStart < yearEnd ||
            (yearStart === yearEnd && monthStart <= monthEnd)
          );
        }

        return true;
      },
      {
        message: t`La date de début ne peut pas être supérieur à la date de fin.`,
        path: ['yearStart'],
      }
    )
    .refine(
      ({ yearStart: startAt, yearEnd: endAt }) => {
        if (startAt && endAt) {
          const yearStart = startAt.getFullYear();
          const monthStart = startAt.getMonth();
          const yearEnd = endAt.getFullYear();
          const monthEnd = endAt.getMonth();

          return (
            yearStart < yearEnd ||
            (yearStart === yearEnd && monthStart <= monthEnd)
          );
        }

        return true;
      },
      {
        message: t`La date de fin ne peut pas être inférieur à la date de début.`,
        path: ['yearEnd'],
      }
    )
    .refine(
      ({ level }) => {
        return !!level;
      },
      {
        message: t`Le niveau est obligatoire.`,
        path: ['level'],
      }
    )
    .refine(
      ({ appellations }) => {
        return appellations.length > 0;
      },
      {
        message: t`Vous devez sélectionner au moins 1 métier.`,
        path: ['appellations'],
      }
    );

export type FormationSchemaT = z.infer<ReturnType<typeof FormationSchema>>;

type Nullable<T = {}> = {
  [K in keyof T]: T[K] | null;
};

export type FormationSchemaNullableT = Nullable<FormationSchemaT>;
